/**
 * Anchoring a Solution API estimate onto a Resource Plan's calendar.
 *
 * The Solution API's contract view labels its months with whatever calendar the
 * estimator happened to draft against ("2025-03" … "2025-08"). Those labels are not
 * the engagement's dates — the planner picks the plan's Start Month, and the estimate's
 * FIRST month lands on it, its second month on the next, and so on. The estimate's
 * length is what fixes the plan's End Month; the planner never picks one.
 *
 * Shared by the import (server) and the New Resource Plan form (client), so the end
 * month the form shows is the one the import writes. Pure `YYYY-MM` integer arithmetic —
 * never parses a Date, so no timezone can move a month.
 */

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function ordinal(month: string): number | null {
  if (!MONTH.test(month)) return null;
  return Number(month.slice(0, 4)) * 12 + (Number(month.slice(5, 7)) - 1);
}

function fromOrdinal(value: number): string {
  return `${Math.floor(value / 12)}-${String((value % 12) + 1).padStart(2, "0")}`;
}

/** `"2026-09"` + 4 → `"2027-01"`. Returns the input unchanged if it is not `YYYY-MM`. */
export function shiftMonth(month: string, delta: number): string {
  const o = ordinal(month);
  return o === null ? month : fromOrdinal(o + delta);
}

export interface EstimateSpan {
  /** The estimate's own first month label, as the Solution API wrote it. */
  firstMonth: string;
  /** The estimate's own last month label. */
  lastMonth: string;
  /** Inclusive count of calendar months from first to last — gaps included. */
  monthCount: number;
}

/** First/last/count of an estimate's months, or null when it has none. */
export function estimateSpan(months: readonly string[]): EstimateSpan | null {
  const ordinals = months.map(ordinal).filter((o): o is number => o !== null);
  if (ordinals.length === 0) return null;
  const first = Math.min(...ordinals);
  const last = Math.max(...ordinals);
  return {
    firstMonth: fromOrdinal(first),
    lastMonth: fromOrdinal(last),
    monthCount: last - first + 1,
  };
}

/**
 * The plan's End Month for an estimate of `monthCount` months starting at `planStart`.
 * A one-month estimate ends where it starts.
 */
export function anchoredEndMonth(planStart: string, monthCount: number): string {
  return shiftMonth(planStart, Math.max(1, monthCount) - 1);
}

/**
 * Re-labels one estimate month onto the plan's calendar: the estimate's first month
 * becomes `planStart`, and every other month keeps its distance from it.
 */
export function anchorEstimateMonth(
  month: string,
  estimateFirstMonth: string,
  planStart: string,
): string {
  const from = ordinal(estimateFirstMonth);
  const to = ordinal(planStart);
  if (from === null || to === null) return month;
  return shiftMonth(month, to - from);
}
